import React from 'react';
import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';
import Heading from '@atlaskit/heading';
import { Flex, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { insightsDevelopmentQuery } from '@atlassian/jira-relay/src/__generated__/insightsDevelopmentQuery.graphql.ts';
import { CriticalVulnInsightWidget } from './critical-vuln-widget';
import { DeploymentCycleTimeInsightWidget } from './deployment-cycle-time-widget';
import { messages } from './messages';
import { OpenPrInsightWidget } from './open-pr-widget';

const Header = () => {
	const { formatMessage } = useIntl();

	return (
		<Heading size="small" as="h2">
			{formatMessage(messages.titleNonFinal)}
		</Heading>
	);
};

type InsightsPanelProps = {
	insightsDevelopmentQueryRef: PreloadedQuery<insightsDevelopmentQuery>;
};

export const InsightsPanel: React.FC<InsightsPanelProps> = ({ insightsDevelopmentQueryRef }) => {
	const insightsData = usePreloadedQuery<insightsDevelopmentQuery>(
		graphql`
			query insightsDevelopmentQuery(
				$cloudId: ID!
				$projectAri: ID!
				$projectId: ID!
				$prsCreatedAfter: DateTime!
				$startFromInclusive: DateTime!
				$endAtExclusive: DateTime!
				$timezoneId: String!
			) @preloadable {
				graphStore @optIn(to: "GraphStore") {
					projectAssociatedPr(
						id: $projectAri
						filter: { and: { to_status: { is: [OPEN] }, createdAt: { after: $prsCreatedAfter } } }
					) @optIn(to: "GraphStoreProjectAssociatedPr") {
						totalCount
					}

					projectAssociatedVulnerability(
						id: $projectAri
						filter: { and: { to_status: { is: [OPEN] }, to_severity: { is: [CRITICAL] } } }
					) @optIn(to: "GraphStoreProjectAssociatedVulnerability") {
						totalCount
					}
				}

				devOpsMetrics {
					cycleTime(
						isIncludeCycleTimeMean: true
						filter: {
							jiraProjectIds: [$projectId]
							cloudId: $cloudId
							startFromInclusive: $startFromInclusive
							endAtExclusive: $endAtExclusive
							timezoneId: $timezoneId
							resolution: { value: 1, unit: WEEK }
						}
					) {
						cycleTimeMetrics {
							data {
								cycleTimeSeconds
							}
						}
					}
				}
			}
		`,
		insightsDevelopmentQueryRef,
	);

	return (
		<Flex direction="column" gap="space.300" alignItems="start" xcss={insightsWrapperStyles}>
			<Header />
			<Flex direction="row" xcss={insightsStyles}>
				<OpenPrInsightWidget value={insightsData.graphStore?.projectAssociatedPr?.totalCount} />
				<CriticalVulnInsightWidget
					value={insightsData.graphStore?.projectAssociatedVulnerability?.totalCount}
				/>
				<DeploymentCycleTimeInsightWidget
					value={
						insightsData.devOpsMetrics?.cycleTime?.cycleTimeMetrics?.[0]?.data?.[0]
							?.cycleTimeSeconds
					}
				/>
			</Flex>
		</Flex>
	);
};

const insightsWrapperStyles = xcss({
	width: '100%',
	paddingLeft: 'space.500',
	paddingRight: 'space.500',
	marginBottom: 'space.400',
});

const insightsStyles = xcss({
	width: '100%',
	gap: 'space.200',
});
